import { capturePrivateSession, isPrivateSessionCurrent } from "@/lib/api/private-session";
import { useInvoiceCommands } from "@/shared/useInvoiceCommands";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { ApiError } from "@/lib/api/client";

import type { KitchenChitView } from "@/lib/api/types";
import type { PrintArtifactAction } from "@/modules/printing/renderDocument";
import type { ReprintReason } from "@/modules/pos/lib/reprintChit";
import { KitchenChit } from "./KitchenChit";

/**
 * One print of one chit, start to finish.
 *
 * Mounted with a ticket id, it does three things in a fixed order and then asks
 * to be unmounted:
 *
 *   1. fetch the chit,
 *   2. dispatch `printTicket` so the SERVER records the paper,
 *   3. `window.print()`.
 *
 * **The order is the design, not an implementation detail.** In print mode the
 * `printTicket` command is also what moves the plates QUEUED → PREPARING and
 * writes the COOKED depletion rows (D18/D19) — printing IS the transition,
 * because printing is the one thing kitchen staff cannot forget to do. So if
 * the command fails, this deliberately does NOT print: paper that the server
 * has no record of means food nobody is charged for and stock that never
 * moved. The till shows the failure and the person taps again; the retry is safe
 * because `dispatchCommand` mints a fresh id for a genuinely new attempt while
 * `sendCommand` reuses one across transport retries.
 *
 * KNOWN LIMITATION — a cancelled print dialog counts as a print.
 * `window.print()` reports nothing about what the user did with the dialog:
 * `afterprint` fires identically whether the paper came out or the dialog was
 * dismissed, and the `print` media query is off again by the time either
 * handler runs. There is no browser API that distinguishes the two, so this
 * job cannot either. In print-actuator mode that matters more than usual: the
 * `printTicket` above has already moved the plates QUEUED -> PREPARING, so a
 * cancelled dialog leaves food marked as started with no paper in the kitchen.
 *
 * The honest recovery is a REPRINT, from the till's «طباعة التذكرة» on the
 * plate's group (gated on `RESTAURANT_KITCHEN_PRINT`, offered for any plate
 * carrying a ticket whatever its state; the PAPER board has no print control,
 * D129). Since D172 the till asks once, only when the ticket already printed,
 * «لم تصل التذكرة» or «نسخة إضافية»; it goes to the station's printer as a
 * durable `reprintTicket` job and comes HERE only when the station has no
 * active printer, with the answer as `action` (ORIGINAL for a paper that
 * never arrived, REPRINT for an extra copy — the only one that says «نسخة
 * مكررة»). No "did it print?" prompt after every chit: friction on every
 * print to catch a rare mis-tap is a worse trade at a service pass.
 *
 * GATE P7 REWORK 2 — THE SERVER SAYS WHAT THIS PAPER IS. A browser chit is a
 * paper the print computer's queue must hear about: its `printTicket` sends
 * the paper it shows (`paperStamp`) and, as a reprint fallback, the till's
 * answer (`reason`, `seenPrintCount`). Under the ticket's job lock the server
 * withdraws the device jobs this paper fulfils — so a print computer that
 * wakes up, or one paired later, never prints them — and answers
 * `kitchenPaper.duplicate`: a device paper may already be in the kitchen, so
 * this one prints «نسخة مكررة». A chit read before a newer paper (409
 * `RESTAURANT_TICKET_PAPER_CHANGED`) reads the ticket again and prints that.
 * A marked chit never carries a remake no paper has carried yet (P9, I5): the
 * answer's `freshItemIds` print first on an unmarked receipt of their own.
 *
 * Mounted only by the till (`InvoicePage` — the reprint fallback and the
 * at-fire chit); the kitchen board prints nothing (D129, P8 removed its dead
 * slot). It renders as a job rather than living inside a click handler
 * because the chit has to be IN THE DOM before `window.print()` runs — the
 * print stylesheet reveals `.receipt` and hides everything else, so a print
 * fired before React committed the markup produces a blank page.
 */
export function ChitPrintJob({
  ticketId,
  action,
  atFire = false,
  reason,
  seenPrintCount,
  announcePrinted = false,
  onDone,
}: {
  ticketId: string;
  /**
   * D172 — what this paper is, from the till's reprint question. Absent (a
   * fire's own chit), the ticket's print count decides, as before.
   */
  action?: PrintArtifactAction;
  /**
   * The till's own chit right after a fire (D18), not a person's request.
   * When the ticket read says a paired print computer serves the station
   * (`deviceServed`), that device prints the fire's ORIGINAL job, so this
   * one stands down: no `printTicket`, no dialog — otherwise the kitchen
   * gets two unmarked papers for one fire (gate P7 QA; D172 I1).
   */
  atFire?: boolean;
  /**
   * Gate P7 rework 2 — this chit is the till's reprint printed here (R3): the
   * person's answer goes with `printTicket` so the server can tell a vouched
   * NOT_RECEIVED from a second paper.
   */
  reason?: ReprintReason;
  seenPrintCount?: number;
  /** Say «طُبعت التذكرة على هذا الجهاز» once the dialog returns (R3). */
  announcePrinted?: boolean;
  /** Called once the job has finished, succeeded or failed. */
  onDone: () => void;
}) {
  const { dispatchCommand } = useInvoiceCommands();
  const toast = usePrivateToast();
  const t = useTranslate();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();

  const { data, error, refetch } = useApiQuery<KitchenChitView>(
    queryKeys.kitchenTicket(ticketId),
    endpoints.restaurant.kitchenTicket(ticketId),
    {
      // Never served from cache. A chit read from a previous print would carry
      // the OLD `printCount` and the reprint banner would be missing from the
      // very paper that needs it.
      staleTime: 0,
      gcTime: 0,
      retry: false,
    },
  );

  // A job fires exactly once. `window.print()` blocks the main thread on most
  // browsers and returns when the dialog closes, but React may still re-run
  // this effect (a refetch, a parent render) — and a second run would print a
  // second chit and increment `printCount` again with nobody asking.
  const fired = useRef(false);
  /** Re-reads after a 409 PAPER_CHANGED: one, then the refusal is a toast. */
  const rereads = useRef(0);
  /** What the server said this paper is; until then, the caller's word. */
  const [paperAction, setPaperAction] = useState<PrintArtifactAction | undefined>(undefined);
  /**
   * P9 (I5) — plates whose current making reaches the kitchen on THIS paper
   * first, on a paper the server marks «نسخة مكررة»: they print on a receipt
   * of their own, unmarked, ahead of the marked one.
   */
  const [freshItemIds, setFreshItemIds] = useState<ReadonlySet<string>>(() => new Set());
  const [lifetime] = useState(capturePrivateSession);
  /*
   * D172 — the job's lifetime is the COMPONENT's, not one effect run's. The
   * effect fires once (`fired`), so tying "still current" to that run's
   * cleanup meant any parent re-render mid-flight (a new `onDone` arrow, a
   * refetch, StrictMode's double invoke) silently cancelled the only print:
   * `printTicket` recorded the paper and no dialog ever opened. Found by the
   * P6 browser run on the till's reprint fallback.
   */
  const mounted = useRef(true);
  const printFrame = useRef<number | undefined>(undefined);
  const latest = useRef({ onDone, toast, errorMessage, queryClient, refetch, t });
  useLayoutEffect(() => {
    latest.current = { onDone, toast, errorMessage, queryClient, refetch, t };
  });
  useEffect(() => {
    mounted.current = true;
    // Belt-and-braces: if the job unmounts mid-flight the scope must not leak
    // onto the next print (which may be a customer receipt).
    return () => {
      mounted.current = false;
      if (printFrame.current !== undefined) {
        cancelAnimationFrame(printFrame.current);
        printFrame.current = undefined;
      }
      delete document.body.dataset.printScope;
    };
  }, []);

  useEffect(() => {
    const current = () => mounted.current && isPrivateSessionCurrent(lifetime);
    if (!current()) return;
    if (error) {
      latest.current.toast.error(latest.current.errorMessage(error));
      latest.current.onDone();
      return;
    }
    if (!data || fired.current) {
      return;
    }
    fired.current = true;
    if (atFire && data.deviceServed === true) {
      latest.current.onDone();
      return;
    }

    void (async () => {
      let invoiceId: string | undefined;
      let serverAction: PrintArtifactAction | undefined;
      let serverFresh: ReadonlySet<string> = new Set();
      try {
        // No `invoiceId`: the server resolves the invoice from the ticket, and
        // the chit payload deliberately does not carry one. See the note on
        // `printTicket` in `invoiceCommands.ts`.
        const result = await dispatchCommand({
          type: "printTicket",
          payload: {
            ticketId,
            ...("paperStamp" in data ? { paperStamp: data.paperStamp ?? null } : {}),
            ...(reason ? { reason } : {}),
            ...(seenPrintCount !== undefined ? { seenPrintCount } : {}),
          },
        });
        invoiceId = result.invoice?.id;
        if (result.kitchenPaper) {
          // The server's word decides, the till's EXTRA_COPY included: an
          // extra copy of a ticket nothing of which reached the kitchen is its
          // only paper and prints unmarked (P9, I5).
          serverAction = result.kitchenPaper.duplicate ? "REPRINT" : "ORIGINAL";
          if (serverAction === "REPRINT") serverFresh = new Set(result.kitchenPaper.freshItemIds ?? []);
        }
      } catch (failure) {
        if (!current()) return;
        if (
          failure instanceof ApiError &&
          failure.code === "RESTAURANT_TICKET_PAPER_CHANGED" &&
          rereads.current < 1 &&
          latest.current.refetch
        ) {
          // The chit on the screen is older than the ticket's paper: read it
          // again and print what it says now (nothing was recorded).
          rereads.current += 1;
          fired.current = false;
          void latest.current.refetch();
          return;
        }
        latest.current.toast.error(latest.current.errorMessage(failure));
        latest.current.onDone();
        return;
      }
      if (!current()) return;
      // The server's word on what this paper is, IN THE DOM before the dialog.
      if (serverAction) {
        flushSync(() => {
          setPaperAction(serverAction);
          setFreshItemIds(serverFresh);
        });
      }
      // One frame, so the browser has actually painted the chit this component
      // just rendered before the print dialog snapshots the page.
      if (!current()) return;
      printFrame.current = requestAnimationFrame(() => {
        printFrame.current = undefined;
        if (!current()) return;
        // Two papers share the `.receipt` print plumbing — this chit and the
        // customer receipt. When a chit prints from the POS invoice page the
        // receipt markup is also in the DOM, and without a scope both would be
        // revealed and print SUPERIMPOSED. The attribute flips the print
        // stylesheet to chit-only for exactly the duration of this dialog.
        document.body.dataset.printScope = "chit";
        try {
          window.print();
        } finally {
          delete document.body.dataset.printScope;
        }
        if (!current()) return;
        if (announcePrinted) {
          latest.current.toast.success(latest.current.t((m) => m.reprintQuestion.printedHere));
        }
        void invalidateInvoiceMutation(latest.current.queryClient, invoiceId);
        latest.current.onDone();
      });
    })();
  }, [data, error, ticketId, lifetime, dispatchCommand, atFire, action, reason, seenPrintCount, announcePrinted]);

  if (!data || (atFire && data.deviceServed === true)) {
    // Nothing visible: the till stays on screen and the button that started
    // this shows its own busy state. A spinner over the whole screen for a
    // sub-second fetch would be more disruptive than the wait.
    return (
      <p className="sr-only" aria-live="polite">
        {t((m) => m.kitchen.printing)}
      </p>
    );
  }

  const fresh = freshItemIds.size > 0 ? data.items.filter((item) => freshItemIds.has(item.id)) : [];
  if (fresh.length > 0) {
    // P9 (I5) — a remake no paper has carried yet never sits under «نسخة
    // مكررة»: it prints first, unmarked, on its own receipt; the plates that
    // may already be in the kitchen follow on the marked one.
    const rest = data.items.filter((item) => !freshItemIds.has(item.id));
    return (
      <div className="kitchen-chit-stack" data-testid="kitchen-chit-stack">
        <KitchenChit chit={{ ...data, items: fresh }} action="ORIGINAL" />
        {rest.length > 0 ? <KitchenChit chit={{ ...data, items: rest }} action="REPRINT" /> : null}
      </div>
    );
  }
  return <KitchenChit chit={data} action={paperAction ?? action} />;
}
